import React, { useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { 
  Building2, Shield, Award, Users, Target, 
  Heart, Lightbulb, Linkedin, Twitter, Mail 
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { 
  GSAPTextReveal, 
  ScrollScaleReveal,
  StaggeredCardsContainer,
  MaskReveal,
  TextScrambleGSAP,
  ParallaxElement,
  NeonGlowLine,
  GSAPCounter
} from '@/components/ui/GSAPAnimations';

gsap.registerPlugin(ScrollTrigger);

const values = [
  {
    icon: Lightbulb,
    title: 'Innovation First',
    description: 'We challenge conventional EdTech approaches with creative solutions.',
    tattva: 'Jiva',
    color: 'rgba(255, 112, 67, 0.5)',
  },
  {
    icon: Shield,
    title: 'Trust & Security',
    description: 'Enterprise-grade security with ISO compliance standards.',
    tattva: 'Samvara',
    color: 'rgba(34, 197, 94, 0.5)',
  },
  {
    icon: Heart,
    title: 'Human-Centered',
    description: 'Technology that amplifies human potential, not replaces it.',
    tattva: 'Moksha',
    color: 'rgba(0, 137, 123, 0.5)',
  },
  {
    icon: Target,
    title: 'Result-Oriented',
    description: 'Every feature is designed with measurable impact in mind.',
    tattva: 'Ajiva',
    color: 'rgba(128, 140, 155, 0.5)',
  },
];

const trustSignals = [
  {
    icon: Building2,
    title: 'Incorporated Entity',
    description: 'Registered Pvt. Ltd. Company under MCA, India',
    visual: 'MOA',
  },
  {
    icon: Shield,
    title: 'ISO Compliant',
    description: 'Following ISO 27001 Security Standards',
    visual: 'ISO',
  },
  {
    icon: Award,
    title: 'Authorized Capital',
    description: 'Backed by substantial capital for sustained growth',
    visual: 'INR',
  },
];

const leadership = [
  {
    name: 'Leo Fernandez',
    role: 'CEO & Founder',
    tattva: 'Driven by Jiva',
    image: '/images/team/leo-fernandez.webp',
  },
  {
    name: 'Bambino Clansmann',
    role: 'COO',
    tattva: 'Driven by Moksha',
    image: '/images/team/bambino-clansmann.jpg',
  },
  {
    name: 'Shankar Nathan',
    role: 'CMO',
    tattva: 'Driven by Bandha',
    image: '/images/team/shankar-nathan.jpeg',
  },
  {
    name: 'Sundaram W',
    role: 'CTO',
    tattva: 'Driven by Ajiva',
    image: '/images/team/sundaram-w.jpg',
  },
];

const teamMembers = [
  {
    name: 'Harish Devarajan',
    role: 'Director of Strategy',
    image: '/images/team/harish-devarajan.webp',
  },
  {
    name: 'Wycliff Daniel',
    role: 'Business Development Manager',
    image: '/images/team/wycliff-daniel.png',
  },
  {
    name: 'Yentl D\'Silva',
    role: 'Operations Lead',
    image: '/images/team/yentl-dsilva.png',
  },
  {
    name: 'Anand Kabra',
    role: 'Senior Advisor',
    image: '/images/team/anand-kabra.webp',
  },
  {
    name: 'Ivan Fernandez',
    role: 'Product Lead',
    image: '/images/team/ivan-fernandez.webp',
  },
  {
    name: 'Siddharth Sood',
    role: 'Technology Advisor',
    image: '/images/team/siddharth-sood.webp',
  },
  {
    name: 'Arun Pereira',
    role: 'Education Consultant',
    image: '/images/team/arun-pereira.webp',
  },
];

const milestones = [
  {
    year: 'Dec 2025',
    event: 'Company Founded',
    description: 'Tattva Techworkz Pvt. Ltd. incorporated with a vision to bridge ancient wisdom and modern technology in education.',
  },
  {
    year: 'Jan 2026',
    event: 'Lynkt Goes Live',
    description: 'Free upskilling platform for teachers launched. Educators begin certifying in modern pedagogy, creating a grassroots network that would become our growth engine.',
  },
  {
    year: 'Feb 2026',
    event: 'Teacher Referral Engine',
    description: 'Teachers using Lynkt start recommending Tattva ERP to their school administrators. Organic referral chain begins scaling — teachers become brand ambassadors.',
  },
  {
    year: 'Mar 2026',
    event: 'LMS for Free Academic Classes',
    description: 'LMS module enables teachers to offer extra academic classes free of cost. Student engagement spikes — teachers gain credibility, schools gain outcomes.',
  },
  {
    year: 'Apr 2026',
    event: '500+ Institutions Onboarded',
    description: 'Crossed 500 institutions across India through teacher-led growth. No cold calling — every client arrived through trusted referrals from educators on the ground.',
  },
  {
    year: 'May 2026',
    event: 'Outcome-Based Education in College ERP',
    description: 'OBE parameters fully integrated — course outcomes, program outcomes, and attainment mapping built into the College ERP. NEP 2020 and NAAC compliance out of the box.',
  },
  {
    year: 'Jun 2026',
    event: 'Edusoft Launches for Global Markets',
    description: 'Affordable, lite version of School ERP branded as Edusoft for international schools. First paying clients in the US, Australia, and UAE within weeks.',
  },
  {
    year: 'Jul 2026',
    event: 'Global Expansion — US, Australia, UAE',
    description: 'Live with paying clients across English-speaking markets. Edusoft serves schools that need powerful infrastructure management without enterprise pricing.',
  },
];

const AboutPage = () => {
  const timelineRef = useRef(null);

  // Timeline Animation
  useEffect(() => {
    if (!timelineRef.current) return;

    const ctx = gsap.context(() => {
      const items = timelineRef.current.querySelectorAll('.timeline-item');
      const line = timelineRef.current.querySelector('.timeline-line');

      // Animate the line
      gsap.fromTo(line, 
        { scaleY: 0 },
        {
          scaleY: 1,
          duration: 1.5,
          ease: 'power2.out',
          scrollTrigger: {
            trigger: timelineRef.current,
            start: 'top 70%',
            end: 'bottom 70%',
            scrub: 1,
          },
        }
      );

      // Animate each item
      items.forEach((item, i) => {
        gsap.fromTo(item,
          { opacity: 0, x: i % 2 === 0 ? -50 : 50 },
          {
            opacity: 1,
            x: 0,
            duration: 0.8,
            ease: 'power3.out',
            scrollTrigger: {
              trigger: item,
              start: 'top 80%',
              toggleActions: 'play none none none',
            },
          }
        );
      });
    }, timelineRef);

    return () => ctx.revert();
  }, []);

  return (
    <div className="pt-20 bg-void">
      {/* ============================================
          HERO SECTION
          ============================================ */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 bg-mesh-gradient" />
        
        {/* Decorative Elements */}
        <ParallaxElement speed={-0.2} className="absolute top-20 right-20 opacity-20">
          <div className="w-96 h-96 border border-teal/20 rounded-full" />
        </ParallaxElement>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Left Content */}
            <div>
              <MaskReveal direction="up">
                <span className="font-mono text-xs text-teal tracking-widest uppercase mb-4 block">
                  Our Story
                </span>
              </MaskReveal>
              
              <GSAPTextReveal 
                text="More Than Software."
                className="text-display text-4xl sm:text-5xl lg:text-6xl text-foreground mb-2"
                splitBy="words"
                staggerDelay={0.1}
              />
              <GSAPTextReveal 
                text="A Philosophy."
                className="text-display text-4xl sm:text-5xl lg:text-6xl text-gradient-teal mb-6"
                splitBy="words"
                staggerDelay={0.1}
                delay={0.5}
              />
              
              <MaskReveal direction="up">
                <p className="text-muted-foreground text-lg leading-relaxed">
                  Founded in December 2025, Tattva Techworkz was born from a single realization: 
                  EdTech was too focused on &apos;Tech&apos; and not enough on &apos;Education&apos;. We integrated 
                  the ancient wisdom of the 7 Tattvas into modern software architecture.
                </p>
              </MaskReveal>
            </div>

            {/* Right - Stats */}
            <StaggeredCardsContainer className="grid grid-cols-2 gap-6">
              {[
                { value: '2025', label: 'Founded', suffix: '' },
                { value: '7', label: 'Tattva Principles', suffix: '' },
                { value: '3', label: 'Core Products', suffix: '' },
                { value: '100', label: 'Team Growing', suffix: '%' },
              ].map((stat, index) => (
                <motion.div
                  key={stat.label}
                  className="glass-card p-6 text-center group hover:border-teal/30 transition-colors"
                  whileHover={{ y: -5, scale: 1.02 }}
                >
                  <div className="text-3xl font-display font-bold text-teal mb-1">
                    <TextScrambleGSAP 
                      text={`${stat.value}${stat.suffix}`}
                      trigger="scroll"
                    />
                  </div>
                  <div className="text-sm text-muted-foreground">{stat.label}</div>
                </motion.div>
              ))}
            </StaggeredCardsContainer>
          </div>
        </div>
      </section>

      {/* ============================================
          PHILOSOPHY SECTION - Ajiva-Jiva Balance
          ============================================ */}
      <section className="py-24 bg-void-light/30 relative overflow-hidden">
        {/* Neon Line */}
        <div className="absolute top-0 left-0 right-0">
          <NeonGlowLine />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollScaleReveal className="text-center mb-16">
            <span className="font-mono text-xs text-orange tracking-widest uppercase mb-4 block">
              Deep Tech Meets Human Potential
            </span>
            <h2 className="text-display text-3xl sm:text-4xl text-foreground mb-4">
              The <span className="text-gradient-orange">Ajiva-Jiva</span> Balance
            </h2>
            <p className="text-muted-foreground text-lg max-w-3xl mx-auto">
              In Jain philosophy, Jiva represents the living soul—creativity, innovation, 
              the human spirit. Ajiva represents the non-living—the technical infrastructure, 
              the code, the systems. We believe true excellence comes from balancing both.
            </p>
          </ScrollScaleReveal>

          {/* Values Grid */}
          <StaggeredCardsContainer className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                className="glass-card p-6 text-center group cursor-pointer"
                style={{ borderColor: value.color }}
                whileHover={{ 
                  y: -10, 
                  boxShadow: `0 0 30px ${value.color}` 
                }}
              >
                <div 
                  className="w-14 h-14 mx-auto mb-4 rounded-xl flex items-center justify-center transition-all"
                  style={{ backgroundColor: value.color }}
                >
                  <value.icon className="w-7 h-7 text-foreground" />
                </div>
                <h3 className="text-display text-lg text-foreground mb-2">
                  {value.title}
                </h3>
                <p className="text-sm text-muted-foreground mb-3">
                  {value.description}
                </p>
                <span className="text-xs font-mono text-orange">
                  [ {value.tattva} ]
                </span>
              </motion.div>
            ))}
          </StaggeredCardsContainer>
        </div>
      </section>

      {/* ============================================
          TRUST SIGNALS
          ============================================ */}
      <section className="py-24 bg-void">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollScaleReveal className="text-center mb-16">
            <span className="font-mono text-xs text-teal tracking-widest uppercase mb-4 block">
              Trust & Compliance
            </span>
            <h2 className="text-display text-3xl sm:text-4xl text-foreground">
              Built on a Foundation of <span className="text-gradient-teal">Trust</span>
            </h2>
          </ScrollScaleReveal>

          <StaggeredCardsContainer className="grid md:grid-cols-3 gap-8">
            {trustSignals.map((signal, index) => (
              <motion.div
                key={signal.title}
                className="glass-card p-8 text-center group"
                whileHover={{ y: -10, scale: 1.02 }}
              >
                {/* Visual Badge */}
                <div className="relative w-24 h-24 mx-auto mb-6">
                  <motion.div 
                    className="absolute inset-0 bg-gradient-teal rounded-full opacity-20"
                    animate={{ scale: [1, 1.1, 1] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <signal.icon className="w-10 h-10 text-teal" />
                  </div>
                  <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 px-3 py-1 bg-void border border-teal/30 rounded-full">
                    <TextScrambleGSAP 
                      text={signal.visual}
                      className="text-xs text-teal"
                      trigger="scroll"
                    />
                  </div>
                </div>

                <h3 className="text-display text-xl text-foreground mb-2">
                  {signal.title}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {signal.description}
                </p>
              </motion.div>
            ))}
          </StaggeredCardsContainer>
        </div>
      </section>

      {/* ============================================
          TIMELINE
          ============================================ */}
      <section className="py-24 bg-void-light/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollScaleReveal className="text-center mb-16">
            <span className="font-mono text-xs text-orange tracking-widest uppercase mb-4 block">
              From Zero to 500+ Institutions
            </span>
            <h2 className="text-display text-3xl sm:text-4xl text-foreground">
              How Teachers <span className="text-gradient-orange">Built Our Growth</span>
            </h2>
            <p className="text-muted-foreground text-base max-w-2xl mx-auto mt-4">
              No cold calls. No aggressive sales. Teachers upskilled on Lynkt, saw the impact,
              and recommended us to their schools. Every institution came through trust.
            </p>
          </ScrollScaleReveal>

          <div ref={timelineRef} className="relative">
            {/* Timeline Line */}
            <div 
              className="timeline-line absolute left-4 md:left-1/2 top-0 bottom-0 w-px origin-top"
              style={{ 
                background: 'linear-gradient(to bottom, hsl(174, 100%, 27%), hsl(14, 100%, 63%), hsl(174, 100%, 27%))' 
              }}
            />

            {milestones.map((milestone, index) => (
              <div
                key={index}
                className={cn(
                  'timeline-item relative flex items-center mb-12',
                  index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                )}
              >
                {/* Dot */}
                <motion.div 
                  className="absolute left-4 md:left-1/2 -translate-x-1/2 w-4 h-4 rounded-full bg-teal border-4 border-void z-10"
                  whileInView={{ scale: [0, 1.2, 1] }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5 }}
                />

                {/* Content */}
                <div className={cn(
                  'ml-12 md:ml-0 md:w-1/2',
                  index % 2 === 0 ? 'md:pr-12 md:text-right' : 'md:pl-12'
                )}>
                  <motion.div
                    className="glass-card p-6 inline-block"
                    whileHover={{ scale: 1.02, y: -5 }}
                  >
                    <span className="font-mono text-sm text-teal">{milestone.year}</span>
                    <h3 className="text-display text-xl text-foreground mt-1">
                      {milestone.event}
                    </h3>
                    <p className="text-sm text-muted-foreground mt-2">
                      {milestone.description}
                    </p>
                  </motion.div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================
          LEADERSHIP
          ============================================ */}
      <section className="py-24 bg-void">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollScaleReveal className="text-center mb-16">
            <span className="font-mono text-xs text-teal tracking-widest uppercase mb-4 block">
              Leadership
            </span>
            <h2 className="text-display text-3xl sm:text-4xl text-foreground">
              The <span className="text-gradient-teal">Minds</span> Behind Tattva
            </h2>
          </ScrollScaleReveal>

          <StaggeredCardsContainer className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {leadership.map((person) => (
              <motion.div
                key={person.name}
                className="group"
                whileHover={{ y: -10 }}
              >
                <div className="glass-card overflow-hidden">
                  <div className="relative h-72 overflow-hidden">
                    <motion.img
                      src={person.image}
                      alt={person.name}
                      className="w-full h-full object-cover object-top transition-all duration-700 grayscale group-hover:grayscale-0"
                      whileHover={{ scale: 1.05 }}
                      transition={{ duration: 0.6 }}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-void via-void/30 to-transparent opacity-60 group-hover:opacity-30 transition-opacity" />
                    <motion.div
                      className="absolute top-4 right-4 px-3 py-1 rounded-full bg-teal/20 backdrop-blur-md border border-teal/30 text-teal text-xs font-mono opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      {person.tattva}
                    </motion.div>
                  </div>
                  <div className="p-5">
                    <h3 className="text-display text-lg text-foreground">
                      {person.name}
                    </h3>
                    <p className="text-teal text-sm font-medium">
                      {person.role}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </StaggeredCardsContainer>
        </div>
      </section>

      {/* ============================================
          TEAM MEMBERS
          ============================================ */}
      <section className="py-24 bg-void-light/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollScaleReveal className="text-center mb-16">
            <span className="font-mono text-xs text-orange tracking-widest uppercase mb-4 block">
              The Team
            </span>
            <h2 className="text-display text-3xl sm:text-4xl text-foreground">
              People Who Make It <span className="text-gradient-orange">Happen</span>
            </h2>
          </ScrollScaleReveal>

          <StaggeredCardsContainer className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6">
            {teamMembers.map((person) => (
              <motion.div
                key={person.name}
                className="group text-center"
                whileHover={{ y: -6 }}
              >
                <div className="relative w-32 h-32 mx-auto mb-4 rounded-2xl overflow-hidden border border-glass-border group-hover:border-teal/40 transition-colors">
                  <img
                    src={person.image}
                    alt={person.name}
                    className="w-full h-full object-cover object-top transition-all duration-500 grayscale group-hover:grayscale-0"
                  />
                </div>
                <h4 className="text-foreground font-display font-semibold text-sm">
                  {person.name}
                </h4>
                <p className="text-xs text-muted-foreground mt-1">
                  {person.role}
                </p>
              </motion.div>
            ))}
          </StaggeredCardsContainer>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
